<script setup lang="ts">
  import { useGetAccountStates } from "@/api/account-state/account-state"
  import { GetUnbilledV2QueryError, useGetUnbilledV2 } from "@/api/bill/bill.ts"
  import { BillGenerationState } from "@/api/model/billGenerationState"
  import { ErrorCode } from "@/api/model/errorCode"
  import { SupplyState } from "@/api/model/supplyState"
  import { MyAccountRoute } from "@/constants/routes/myAccount.ts"
  import { aggregateQueries } from "@/lib/aggregateQueries"
  import { useGetAccountId } from "@/lib/useGetAccountId"
  import ErrorReporter from "@/services/ErrorReporter"
  import dayjs from "dayjs"
  import { computed } from "vue"
  import ErrorMessage from "@/components/ErrorMessage.vue"
  import { Button } from "@/components/ui/button"
  import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
  } from "@/components/ui/card"
  import Icon from "@/components/ui/icons/Icon.vue"
  import { Link } from "@/components/ui/link"
  import PlainTooltip from "@/components/ui/plain-tooltip/PlainTooltip.vue"

  import EnergyUsageCardSkeleton from "./EnergyUsageCardSkeleton.vue"

  const pendingBillProcessingWindow = 4 // days
  const blank = "--.--"
  const accountIdQuery = useGetAccountId()
  const unbilledQuery = useGetUnbilledV2(
    accountIdQuery.accountId,
    { includeStandingCharges: false },
    {
      query: {
        throwOnError: (error: GetUnbilledV2QueryError) => {
          const errorCode = error?.response?.data?.errorCode
          const isExpectedServerError = errorCode === ("notFound" as ErrorCode)
          if (!isExpectedServerError) ErrorReporter.report(error)
          return false
        },
      },
    },
  )
  const accountStateQuery = useGetAccountStates(accountIdQuery.accountId)

  const { isError, isFetching, isLoading } = aggregateQueries([
    accountIdQuery,
    unbilledQuery,
    accountStateQuery,
  ])

  const summary = computed(() => {
    const nextBill = unbilledQuery.data?.value?.nextBill
    const isElecEstimated = Boolean(nextBill?.electricityUsage.isEstimated)
    const isGasEstimated = Boolean(nextBill?.gasUsage.isEstimated)
    const isEstimated = isElecEstimated || isGasEstimated

    const formatConsumption = (
      consumption: number | undefined,
      isEstimated: boolean,
    ) => {
      if (!consumption) return blank
      return isEstimated ? consumption.toFixed(0) : consumption.toFixed(2)
    }

    return {
      dateRange: nextBill
        ? `${dayjs(nextBill.startDate).format("D MMM")} - Today`
        : "",
      totalCostInPounds: nextBill?.totalCostInPounds.toFixed(2) ?? blank,
      totalConsumptionInKwh: formatConsumption(
        nextBill?.totalConsumptionInKwh,
        isEstimated,
      ),
      electricityUsage: {
        isSupplied: nextBill?.electricityUsage.isSupplied,
        usageCostInPounds:
          nextBill?.electricityUsage.usageCostInPounds.toFixed(2),
        consumptionInKwh: formatConsumption(
          nextBill?.electricityUsage.consumptionInKwh,
          isElecEstimated,
        ),
      },
      gasUsage: {
        isSupplied: nextBill?.gasUsage.isSupplied,
        usageCostInPounds: nextBill?.gasUsage.usageCostInPounds.toFixed(2),
        consumptionInKwh: formatConsumption(
          nextBill?.gasUsage.consumptionInKwh,
          isGasEstimated,
        ),
      },
      isEstimated,
    }
  })

  const estimatedCopy = computed(() =>
    summary.value.isEstimated
      ? "We've calculated your estimated usage for this period using your previous meter reads."
      : "We've calculated your up to date usage from your last bill until today.",
  )

  const nextBillIsBeingProcessed = computed(() => {
    const pendingBill = unbilledQuery.data.value?.pendingBill
    if (pendingBill) {
      const endDate = dayjs(pendingBill.endDate)
      return endDate.isAfter(
        dayjs(new Date()).subtract(pendingBillProcessingWindow, "days"),
      )
    }
    return false
  })

  const unableToGenerateEstimate = computed(() => {
    const nextBill = unbilledQuery.data?.value?.nextBill

    const unableToEstimateSuppliedElectricityUsage =
      nextBill?.electricityUsage.unableToEstimate
    const unableToEstimateSuppliedGasUsage = nextBill?.gasUsage.unableToEstimate
    return (
      accountStateQuery.data?.value?.billGeneration ===
        BillGenerationState.noBillNewAccount ||
      unableToEstimateSuppliedElectricityUsage ||
      unableToEstimateSuppliedGasUsage
    )
  })

  const issueWithBillsNoEstimate = computed(() => {
    const nextBill = unbilledQuery.data?.value?.nextBill
    const hasNoNextBill = !nextBill
    const noBillWithin31Days =
      accountStateQuery.data?.value?.billGeneration ===
      BillGenerationState.noBillWithin31Days
    return hasNoNextBill || noBillWithin31Days
  })

  const isOnboarding = computed(() => {
    return (
      accountStateQuery.data?.value?.supply === SupplyState.onboarding ||
      accountStateQuery.data?.value?.supply === SupplyState.preOnboarding
    )
  })

  const willHaveFinalBill = computed(() => {
    return (
      accountStateQuery.data?.value?.supply === SupplyState.lossComplete ||
      accountStateQuery.data?.value?.supply === SupplyState.movedOut
    )
  })

  const isNotOnSupply = computed(() => {
    return (
      summary.value.electricityUsage.isSupplied === false &&
      summary.value.gasUsage.isSupplied === false
    )
  })
</script>

<template>
  <EnergyUsageCardSkeleton v-if="isLoading" />
  <Card v-else data-testid="your-usage-card">
    <CardHeader>
      <CardTitle>Energy Usage</CardTitle>
    </CardHeader>
    <CardContent>
      <ErrorMessage
        v-if="
          isError &&
          !isFetching &&
          accountStateQuery.data?.value?.billGeneration !==
            BillGenerationState.noBillNewAccount
        "
        :message="null"
      />
      <div v-if="isNotOnSupply">
        <p>
          Recent energy usage is unavailable as your meters are not currently on
          supply.
        </p>
      </div>
      <div v-else-if="isOnboarding">
        <p>
          We'll show your latest usage here as soon as we have enough readings
          from your meter.
        </p>
      </div>
      <div v-else-if="willHaveFinalBill">
        <p>
          You'll soon receive your final bill where you can view your usage.
        </p>
      </div>
      <div v-else-if="nextBillIsBeingProcessed">
        Your next bill is being processed. You'll receive it in your inbox
        within the coming days.
      </div>
      <div v-else-if="unableToGenerateEstimate || issueWithBillsNoEstimate">
        <p v-if="unableToGenerateEstimate">
          It looks like we don't have enough reads to generate an estimate.
          Submit regular reads to better understand your energy usage.
        </p>
        <p v-else>
          We can’t calculate your recent energy usage just yet as we’re
          currently updating your bills.
        </p>
        <div class="space-y-0.5">
          <p class="body-text-strong mb-1 mt-4 sm:mt-2">Total</p>
          <p>£{{ blank }} | {{ blank }} kWh</p>
        </div>
      </div>

      <div v-else>
        <div class="space-y-0.5">
          <p class="body-text-strong flex items-center gap-1">
            <span class="h-full">Total ({{ summary.dateRange }})</span>

            <PlainTooltip trigger-label="About estimated usage">
              {{ estimatedCopy }}
            </PlainTooltip>
          </p>

          <p
            v-if="summary.isEstimated"
            class="body-text-strong text-secondary-500"
          >
            Estimated Usage
          </p>
          <p>
            {{
              `${summary.totalConsumptionInKwh} kWh | £${summary.totalCostInPounds}`
            }}
          </p>
        </div>

        <div class="my-4 flex">
          <div v-if="summary.electricityUsage.isSupplied" class="w-full">
            <dl class="relative pl-5">
              <Icon
                name="Electricity"
                class="absolute left-0 top-1 row-span-3 stroke-icon-primary sm:top-1.5"
              />
              <dt class="body-text-strong">Electricity</dt>
              <dd data-testid="next-bill-electricity-consumption-in-kwh">
                {{ `${summary.electricityUsage.consumptionInKwh} kWh` }}
              </dd>
              <dd data-testid="next-bill-electricity-usage-cost-in-pounds">
                £{{ summary.electricityUsage.usageCostInPounds }}
              </dd>
            </dl>
          </div>
          <div v-if="summary.gasUsage.isSupplied" class="w-full">
            <dl class="relative pl-5">
              <Icon
                name="Gas"
                class="absolute left-0 top-1 row-span-3 stroke-icon-secondary sm:top-1.5"
              />
              <dt class="body-text-strong">Gas</dt>
              <dd data-testid="next-bill-gas-consumption-in-kwh">
                {{ `${summary.gasUsage.consumptionInKwh} kWh` }}
              </dd>
              <dd data-testid="next-bill-gas-usage-cost-in-pounds">
                £{{ summary.gasUsage.usageCostInPounds }}
              </dd>
            </dl>
          </div>
        </div>
        <p class="label-text">
          All rates incl. VAT, excluding standing charges.
        </p>
      </div>
    </CardContent>
    <CardFooter>
      <Button variant="tonal" as-child>
        <Link :to="MyAccountRoute.usage">View Energy Usage</Link>
      </Button>
    </CardFooter>
  </Card>
</template>
